import { useEffect, useState } from "react";
import api from "../../lib/api";
import { PageHeader } from "../../components/PortalShell";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  RefreshCw, CheckCircle2, AlertTriangle, Database, Zap,
  CreditCard, Truck, Landmark, Plug, Clock, FlaskConical, ShieldCheck, Sparkles,
  Shield, Banknote, ShoppingBag, Mail, MessageSquare,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// AdminIntegrations — operator console for swapping mock providers ↔ live APIs.
//
// Right now, only the Catalog provider is wired to a real upstream
// (custom wholesale API, 300 calls/24h hard limit). Payments, Credit,
// and Shipping rows are placeholders ready for their respective providers.
// ─────────────────────────────────────────────────────────────────────────────
export default function AdminIntegrations() {
  const [catalog, setCatalog] = useState(null);
  const [shipping, setShipping] = useState(null);
  const [plaid, setPlaid] = useState(null);
  const [middesk, setMiddesk] = useState(null);
  const [ai, setAi] = useState(null);
  const [envSummary, setEnvSummary] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  const [tabapay, setTabapay] = useState(null);
  const [extras, setExtras] = useState(null);
  const loadStatus = () => Promise.all([
    api.get("/admin/catalog/sync-status").then(r => setCatalog(r.data)).catch(() => {}),
    api.get("/admin/integrations/tabapay/status").then(r => setTabapay(r.data)).catch(() => {}),
    api.get("/admin/integrations/shipping/status").then(r => setShipping(r.data)).catch(() => {}),
    api.get("/admin/integrations/plaid/status").then(r => setPlaid(r.data)).catch(() => {}),
    api.get("/admin/integrations/middesk/status").then(r => setMiddesk(r.data)).catch(() => {}),
    api.get("/admin/integrations/ai/status").then(r => setAi(r.data)).catch(() => {}),
    api.get("/admin/integrations/environment").then(r => setEnvSummary(r.data)).catch(() => {}),
    api.get("/admin/integrations/extras/status").then(r => setExtras(r.data)).catch(() => {}),
  ]);

  useEffect(() => { loadStatus(); }, []);

  const runSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/catalog/sync");
      setLastSync(data);
      if (data.ok) {
        toast.success(`✓ Synced ${data.inserted + data.updated} products in ${data.elapsed_ms}ms`);
      } else {
        toast.error(data.reason || "Sync returned no items");
      }
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Integrations"
        subtitle="Swap mock providers for live API integrations · system runs the same routes either way"
      />
      <div className="p-8 space-y-6">
        {/* Environment / Production-mode overview */}
        <EnvOverview env={envSummary}/>

        {/* Catalog row */}
        <IntegrationRow
          icon={Database}
          name="Wholesale Catalog API"
          desc="Real upstream product database · 300 calls per 24h · 6h response cache"
          status={catalog?.synced ? "live" : (catalog?.provider === "wholesale_api" ? "wired" : "mock")}
          provider={catalog?.provider || "noop"}
        >
          {catalog?.provider === "wholesale_api" && (
            <>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <Tile l="Synced products"   v={catalog?.count?.toLocaleString() || "0"}/>
                <Tile l="Provider"          v="wholesale_api"/>
                <Tile l="Rate limit"        v="300 / 24h"/>
              </div>
              <div className="flex items-center gap-2.5 flex-wrap">
                <Button onClick={runSync} disabled={syncing}
                  data-testid="catalog-sync-btn"
                  className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-10 px-4 disabled:opacity-50">
                  <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${syncing ? "animate-spin" : ""}`}/>
                  {syncing ? "Syncing…" : (catalog?.synced ? "Re-sync now" : "Pull catalog now")}
                </Button>
                <span className="text-xs text-foreground/55">
                  Each sync uses 1 API call per page (250 items/page) · counts against the 300/24h limit
                </span>
              </div>

              {lastSync && (
                <div className={`mt-4 rounded-xl border p-4 ${lastSync.ok ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="catalog-sync-result">
                  <div className="flex items-center gap-2 mb-2">
                    {lastSync.ok
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-700"/>
                      : <AlertTriangle className="w-4 h-4 text-amber-700"/>}
                    <div className="font-medium text-sm">
                      {lastSync.ok
                        ? `Synced ${lastSync.total_seen.toLocaleString()} items in ${lastSync.elapsed_ms} ms`
                        : "Sync incomplete"}
                    </div>
                  </div>
                  {lastSync.ok ? (
                    <div className="grid grid-cols-3 gap-3 text-xs">
                      <div><span className="text-foreground/55">Inserted</span><div className="display text-base text-emerald-700">{lastSync.inserted}</div></div>
                      <div><span className="text-foreground/55">Updated</span><div className="display text-base">{lastSync.updated}</div></div>
                      <div><span className="text-foreground/55">Skipped (no SKU)</span><div className="display text-base text-foreground/70">{lastSync.skipped_no_sku}</div></div>
                    </div>
                  ) : (
                    <div className="text-xs text-amber-900">{lastSync.reason}</div>
                  )}
                  {lastSync.rate_limit_hint && <div className="text-[10px] text-foreground/55 mt-2">{lastSync.rate_limit_hint}</div>}
                </div>
              )}
            </>
          )}

          {catalog?.last_sample && (
            <div className="mt-4">
              <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50 mb-2">Sample synced product</div>
              <div className="rounded-xl border border-border p-3 bg-secondary/30 text-sm grid sm:grid-cols-4 gap-2">
                <div><div className="text-[10px] text-foreground/55">SKU</div><div className="mono">{catalog.last_sample.sku}</div></div>
                <div><div className="text-[10px] text-foreground/55">Name</div><div className="truncate">{catalog.last_sample.name}</div></div>
                <div><div className="text-[10px] text-foreground/55">Brand</div><div>{catalog.last_sample.brand || "—"}</div></div>
                <div><div className="text-[10px] text-foreground/55">Asking</div><div className="display">${catalog.last_sample.price?.toFixed(2)}</div></div>
              </div>
            </div>
          )}
        </IntegrationRow>

        {/* Payments + Wallet + Card & ACH — TabaPay (replaces legacy stack · Feb 2026) */}
        <IntegrationRow
          icon={CreditCard}
          name="Payments · Wallet · Card · ACH · RTP"
          desc="TabaPay · single rail for card processing, ACH/RTP payouts, and ledger sub-accounts"
          status={
            tabapay?.live_ping?.ok ? "live"
              : tabapay?.configured ? "wired"
              : "mock"
          }
          provider={tabapay?.configured ? `TabaPay · ${tabapay.env}` : "—"}
        >
          {tabapay && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="tabapay-kpis">
                <Tile l="Transactions 24h"      v={String(tabapay.kpi_24h?.transactions ?? 0)}/>
                <Tile l="Failures 24h"          v={String(tabapay.kpi_24h?.failures ?? 0)}/>
                <Tile l="Webhook events 24h"    v={String(tabapay.kpi_24h?.webhook_events ?? 0)}/>
                <Tile l="Bad-signature events"  v={String(tabapay.kpi_24h?.webhook_bad_signature ?? 0)}/>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Tile l="FQDN"            v={tabapay.has_fqdn ? tabapay.fqdn : "default"}/>
                <Tile l="Bearer token"    v={tabapay.has_bearer ? "configured" : "missing"}/>
                <Tile l="Webhook secret"  v={tabapay.has_webhook_secret ? "configured" : "missing"}/>
                <Tile l="SSO public key"  v={tabapay.has_sso_keys ? "configured" : "missing"}/>
              </div>
              {tabapay.configured ? (
                <div className={`rounded-xl border p-3 text-[11px] leading-relaxed ${
                  tabapay.live_ping?.ok
                    ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                    : "bg-amber-50 border-amber-200 text-amber-900"
                }`} data-testid="tabapay-readiness">
                  {tabapay.live_ping?.ok ? (
                    <>
                      <strong>Live in {tabapay.env}.</strong>{" "}
                      Client path: <span className="mono">{tabapay.client_path}</span>.{" "}
                      Card pay-ins, ACH/RTP payouts, and the local liability ledger route through TabaPay.{" "}
                      Webhook URL: <span className="mono">/api/webhooks/tabapay</span> (register this in the TabaPay dashboard).
                    </>
                  ) : (
                    <>
                      <strong>Configured but live ping failed.</strong>{" "}
                      Bearer token + client ID present, but TabaPay returned{" "}
                      <span className="mono">{tabapay.live_ping?.status || tabapay.live_ping?.error || "error"}</span>.{" "}
                      Verify the credentials in the TabaPay dashboard.
                    </>
                  )}
                </div>
              ) : (
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-[11px] leading-relaxed text-amber-900" data-testid="tabapay-unconfigured">
                  <strong>TabaPay not configured.</strong>{" "}
                  Contact <span className="mono">help@tabapay.com</span> for sandbox onboarding: you'll receive an FQDN, a 22-char ClientID, a Bearer token, and IP-whitelist confirmation.{" "}
                  Then add these to <span className="mono">backend/.env</span> and restart:{" "}
                  <span className="mono">TABAPAY_FQDN</span>,{" "}
                  <span className="mono">TABAPAY_PORT</span>,{" "}
                  <span className="mono">TABAPAY_BEARER_TOKEN</span>,{" "}
                  <span className="mono">TABAPAY_CLIENT_ID</span>,{" "}
                  <span className="mono">TABAPAY_SUBCLIENT_ID</span> (optional),{" "}
                  <span className="mono">TABAPAY_WEBHOOK_SECRET</span>,{" "}
                  <span className="mono">TABAPAY_SSO_KEY_ID</span>,{" "}
                  <span className="mono">TABAPAY_SSO_PUBLIC_KEY</span>.{" "}
                  Until then all wallet ops use the local fallback ledger and card/ACH endpoints return a clean 503.
                </div>
              )}
            </>
          )}
        </IntegrationRow>

        {/* Two.inc — BNPL Net-30/60/90 credit terms (real status) */}
        <IntegrationRow
          icon={Landmark}
          name="Credit & Terms"
          desc="Two.inc · KYB, BNPL Net-30/60/90 underwriting · per-order credit insurance"
          status={extras?.two_inc?.configured ? "live" : "mock"}
          provider={extras?.two_inc?.configured ? `Two.inc · ${extras.two_inc.env}` : "—"}
        >
          {extras?.two_inc && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3" data-testid="two-kpis">
                <Tile l="API key"        v={extras.two_inc.has_key ? "configured" : "missing"}/>
                <Tile l="Merchant ID"    v={extras.two_inc.has_merchant_id ? "configured" : "missing"}/>
                <Tile l="Orders 30d"     v={String(extras.two_inc.orders_30d || 0)}/>
                <Tile l="Webhooks 7d"    v={String(extras.two_inc.webhooks_7d || 0)}/>
              </div>
              <div className={`rounded-xl border p-3 text-[11px] leading-relaxed ${
                extras.two_inc.configured ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-200 text-amber-900"
              }`} data-testid="two-readiness">
                {extras.two_inc.configured ? (
                  <><strong>Live in {extras.two_inc.env}.</strong> Buyers can checkout with Net-30/60/90 terms · Two.inc carries the credit risk + insurance.</>
                ) : (
                  <><strong>Two.inc not configured.</strong> Set <span className="mono">TWO_API_KEY</span> + <span className="mono">TWO_MERCHANT_ID</span> in <span className="mono">backend/.env</span>.</>
                )}
              </div>
            </>
          )}
        </IntegrationRow>

        {/* Allianz Trade — credit insurance */}
        <IntegrationRow
          icon={Shield}
          name="Credit Insurance"
          desc="Allianz Trade · per-buyer cover limits · auto-decision · daily cover-reconciliation"
          status={extras?.allianz?.configured ? "live" : "mock"}
          provider={extras?.allianz?.configured ? `Allianz · ${extras.allianz.env}` : "—"}
        >
          {extras?.allianz && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3" data-testid="allianz-kpis">
                <Tile l="API key"       v={extras.allianz.has_key ? "configured" : "missing"}/>
                <Tile l="Covers 24h"    v={String(extras.allianz.covers_24h || 0)}/>
                <Tile l="Webhooks 7d"   v={String(extras.allianz.webhooks_7d || 0)}/>
                <Tile l="Bad sig 7d"    v={String(extras.allianz.bad_sig_7d || 0)}/>
              </div>
              <div className={`rounded-xl border p-3 text-[11px] leading-relaxed ${
                extras.allianz.configured ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-200 text-amber-900"
              }`} data-testid="allianz-readiness">
                {extras.allianz.configured ? (
                  <><strong>Live in {extras.allianz.env}.</strong> Base URL: <span className="mono">{extras.allianz.base_url}</span>. Webhooks land at <span className="mono">/api/webhooks/allianz</span>.</>
                ) : (
                  <><strong>Allianz not configured.</strong> Set <span className="mono">ALLIANZ_API_KEY</span> + <span className="mono">ALLIANZ_BASE_URL</span> + <span className="mono">ALLIANZ_BROKER_CODE</span> in <span className="mono">backend/.env</span>.</>
                )}
              </div>
            </>
          )}
        </IntegrationRow>

        {/* Shopify — dropship store sync */}
        <IntegrationRow
          icon={ShoppingBag}
          name="Shopify"
          desc="Custom app · dropship store linking · order ingestion · fulfillment sync"
          status={extras?.shopify?.configured ? (extras.shopify.connected_stores > 0 ? "live" : "wired") : "mock"}
          provider={extras?.shopify?.configured ? `Shopify · v${extras.shopify.api_version}` : "—"}
        >
          {extras?.shopify && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3" data-testid="shopify-kpis">
                <Tile l="API key"         v={extras.shopify.has_key ? "configured" : "missing"}/>
                <Tile l="API secret"      v={extras.shopify.has_secret ? "configured" : "missing"}/>
                <Tile l="Connected stores" v={String(extras.shopify.connected_stores || 0)}/>
                <Tile l="Webhooks 24h"    v={String(extras.shopify.webhooks_24h || 0)}/>
              </div>
              <div className={`rounded-xl border p-3 text-[11px] leading-relaxed ${
                extras.shopify.configured ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-200 text-amber-900"
              }`} data-testid="shopify-readiness">
                {extras.shopify.configured ? (
                  <><strong>Custom app configured.</strong> Sellers can connect Shopify stores at <span className="mono">/seller/integrations</span>. API version <span className="mono">{extras.shopify.api_version}</span>.</>
                ) : (
                  <><strong>Shopify not configured.</strong> Set <span className="mono">SHOPIFY_API_KEY</span> + <span className="mono">SHOPIFY_API_SECRET</span> + <span className="mono">SHOPIFY_WEBHOOK_SECRET</span> in <span className="mono">backend/.env</span>.</>
                )}
              </div>
            </>
          )}
        </IntegrationRow>

        {/* Resend — transactional email */}
        <IntegrationRow
          icon={Mail}
          name="Transactional Email"
          desc="Resend · auth emails · invoice notifications · adverse-action notices · digest reports"
          status={extras?.resend?.configured ? "live" : "mock"}
          provider={extras?.resend?.configured ? `Resend · ${extras.resend.from_email}` : "—"}
        >
          {extras?.resend && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3" data-testid="resend-kpis">
                <Tile l="API key"      v={extras.resend.has_key ? "configured" : "missing"}/>
                <Tile l="From email"   v={extras.resend.has_from ? "configured" : "missing"}/>
                <Tile l="Sent 24h"     v={String(extras.resend.sent_24h || 0)}/>
                <Tile l="Failed 24h"   v={String(extras.resend.failed_24h || 0)}/>
              </div>
              <div className={`rounded-xl border p-3 text-[11px] leading-relaxed ${
                extras.resend.configured ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-200 text-amber-900"
              }`} data-testid="resend-readiness">
                {extras.resend.configured ? (
                  <><strong>Live.</strong> All transactional + auth + compliance emails route through Resend. From: <span className="mono">{extras.resend.from_email}</span>.</>
                ) : (
                  <><strong>Resend not configured.</strong> Set <span className="mono">RESEND_API_KEY</span> + <span className="mono">RESEND_FROM_EMAIL</span> in <span className="mono">backend/.env</span>. Without these, all emails silently no-op.</>
                )}
              </div>
            </>
          )}
        </IntegrationRow>

        {/* Twilio — SMS step-up + verify */}
        <IntegrationRow
          icon={MessageSquare}
          name="SMS & Verify"
          desc="Twilio Verify · admin MFA step-up · seller phone trust · banking high-risk gates"
          status={extras?.twilio?.configured ? "live" : "mock"}
          provider={extras?.twilio?.configured ? "Twilio Verify" : "—"}
        >
          {extras?.twilio && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3" data-testid="twilio-kpis">
                <Tile l="Account SID"   v={extras.twilio.has_sid ? "configured" : "missing"}/>
                <Tile l="Auth token"    v={extras.twilio.has_token ? "configured" : "missing"}/>
                <Tile l="Verify SID"    v={extras.twilio.has_verify_sid ? "configured" : "missing"}/>
                <Tile l="Verifies 24h"  v={String(extras.twilio.verifies_24h || 0)}/>
              </div>
              <div className={`rounded-xl border p-3 text-[11px] leading-relaxed ${
                extras.twilio.configured ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-200 text-amber-900"
              }`} data-testid="twilio-readiness">
                {extras.twilio.configured ? (
                  <><strong>Live.</strong> SMS step-up powers admin MFA + seller banking high-risk gates via Twilio Verify.</>
                ) : (
                  <><strong>Twilio not configured.</strong> Set <span className="mono">TWILIO_ACCOUNT_SID</span> + <span className="mono">TWILIO_AUTH_TOKEN</span> + <span className="mono">TWILIO_VERIFY_SERVICE_SID</span> in <span className="mono">backend/.env</span>.</>
                )}
              </div>
            </>
          )}
        </IntegrationRow>

        {/* Middesk — KYB / Business credit at buyer signup */}
        <IntegrationRow
          icon={ShieldCheck}
          name="KYB / Business Verification"
          desc="Middesk · Sec of State, EIN, watchlist, UCC liens, court records, business credit"
          status={
            middesk?.health?.ok && middesk?.health?.status !== 401 ? "live"
              : middesk?.configured ? "wired"
              : "mock"
          }
          provider={middesk?.configured ? `Middesk · ${middesk.env}` : "—"}
        >
          {middesk?.configured && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <Tile l="Total KYBs"     v={String(middesk.kyb_records_total || 0)}/>
                <Tile l="Pending"        v={String(middesk.kyb_pending || 0)}/>
                <Tile l="Approved"       v={String(middesk.kyb_approved || 0)}/>
                <Tile l="Rejected"       v={String(middesk.kyb_rejected || 0)}/>
                <Tile l="Errors"         v={String(middesk.kyb_errors || 0)}/>
              </div>
              {middesk.health?.status === 401 ? (
                <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-[11px] leading-relaxed text-rose-900" data-testid="middesk-401">
                  <strong>API key returns 401 Unauthorized.</strong> Middesk's API rejected the key in <span className="mono">MIDDESK_API_KEY</span>.
                  Either the key isn't activated yet, has been rotated, or your account isn't enabled for live API access.
                  Reach out to <a href="mailto:support@middesk.com" className="underline">support@middesk.com</a> to confirm activation —
                  the integration code is wired correctly and will work the moment the key authenticates.
                </div>
              ) : (
                <div className={`rounded-xl border p-3 text-[11px] leading-relaxed ${middesk.env === "production" ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-blue-50 border-blue-200 text-blue-900"}`}>
                  <strong>Live in {middesk.env}.</strong> KYB runs automatically when buyers register at <span className="mono">/register</span> —
                  legal name + EIN + address + owner are submitted to Middesk for verification. Results stream back via webhook to <span className="mono">/api/webhooks/middesk</span>.
                </div>
              )}
            </>
          )}
        </IntegrationRow>

        {/* AI Underwriter — Verqo AI-generated credit memos */}
        <IntegrationRow
          icon={Sparkles}
          name="AI Credit Underwriter"
          desc="Verqo AI · auto-generates structured credit memos from KYB + Plaid signals"
          status={ai?.configured ? "live" : "mock"}
          provider={ai?.configured ? `${ai.provider} · ${ai.model}` : "—"}
        >
          {ai?.configured && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Tile l="Memos generated"  v={String(ai.memos_total || 0)}/>
                <Tile l="Last 24h"         v={String(ai.memos_today || 0)}/>
                <Tile l="Auto-grants"      v={String(ai.auto_grants_total || 0)}/>
                <Tile l="Auto today"       v={String(ai.auto_grants_today || 0)}/>
              </div>
              <AutoGrantPanel current={ai.auto_grant} onSaved={loadStatus}/>
              <div className="rounded-xl bg-blue-50 border border-blue-200 p-3 text-[11px] leading-relaxed text-blue-900 mt-4">
                <strong>How it works.</strong> Each "Generate AI memo" run on a buyer triggers the rules engine.
                If the AI's decision, confidence, recommended terms, and limit all clear the thresholds you set above,
                the buyer's credit limit is raised <strong>automatically</strong> — no admin click required.
                Buyers see "Pre-approved · Net-30" on their dashboard within seconds of generation.
                You can revoke any individual grant from the buyers list.
              </div>
            </>
          )}
        </IntegrationRow>

        {/* Plaid — buyer bank connection, ACH, identity, cashflow */}
        <IntegrationRow
          icon={Landmark}
          name="Bank Connect & Cashflow"
          desc="Plaid · Auth (ACH numbers), Identity (KYC), Transactions (cashflow underwriting)"
          status={plaid?.configured ? (plaid.env === "production" ? "live" : "wired") : "mock"}
          provider={plaid?.configured ? `Plaid · ${plaid.env}` : "—"}
        >
          {plaid?.configured && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Tile l="Items connected"  v={String(plaid.items_connected || 0)}/>
                <Tile l="Connected today"  v={String(plaid.items_today || 0)}/>
                <Tile l="ACH methods (Plaid)" v={String(plaid.ach_methods_via_plaid || 0)}/>
                <Tile l="Products"         v={(plaid.products || []).join(" · ") || "—"}/>
              </div>
              <div className="rounded-xl bg-blue-50 border border-blue-200 p-3 text-[11px] leading-relaxed text-blue-900">
                <strong>Live in {plaid.env}.</strong> Buyers can link their business bank account in 30 seconds. We pull verified routing/account numbers (instant ACH via Modern Treasury), identity (KYC fraud check), and 24-month transaction history (cashflow underwriting).
                {plaid.env !== "production" && <> To go live, set <span className="mono">PLAID_ENV=production</span> + production secret in <span className="mono">backend/.env</span>.</>}
              </div>
            </>
          )}
        </IntegrationRow>

        {/* Shipping (Parcel via Verqo Shipping · Freight via Freightos pending) */}
        <IntegrationRow
          icon={Truck}
          name="Shipping (Parcel + LTL/FTL)"
          desc="Verqo Shipping for parcel · Freightos slot reserved for LTL/FTL"
          status={
            shipping?.parcel?.health?.ok ? "live"
              : shipping?.parcel?.configured ? "wired"
              : "mock"
          }
          provider={
            shipping?.parcel?.configured
              ? `Verqo Shipping · ${shipping.parcel.test_mode ? "test" : "production"}`
              : "—"
          }
        >
          {shipping?.parcel?.configured && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                <Tile l="Parcel · Verqo Shipping"   v={shipping.parcel.health?.ok ? "✓ live" : "× down"}/>
                <Tile l="Mode"              v={shipping.parcel.test_mode ? "test" : "production"}/>
                <Tile l="Labels purchased"  v={String(shipping.labels_purchased_today || 0)}/>
              </div>
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-[11px] leading-relaxed text-emerald-900 mb-3">
                <strong>Verqo Shipping live in {shipping.parcel.test_mode ? "test" : "production"}.</strong> Multi-carrier rate shopping, label purchase (USPS · UPS · FedEx · DHL), live tracking, and label voiding wired into <span className="mono">/api/shipping/rates · /api/shipping/book · /api/shipping/track/{"{"}carrier{"}"}/{"{"}tracking{"}"}</span>.
                {shipping.parcel.test_mode && <> For UPS/FedEx labels, <a href="https://apps.goshippo.com/settings/carriers" target="_blank" rel="noreferrer" className="underline">activate them in your Verqo Shipping dashboard</a>. USPS works out of the box.</>}
              </div>
              <div className={`rounded-xl border p-3 text-[11px] leading-relaxed ${shipping.freight?.configured ? "bg-blue-50 border-blue-200 text-blue-900" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
                <div className="flex items-center gap-2 mb-1">
                  {shipping.freight?.configured
                    ? <CheckCircle2 className="w-3.5 h-3.5"/>
                    : <AlertTriangle className="w-3.5 h-3.5"/>}
                  <strong>LTL/FTL · Warp</strong>
                  <span className="chip text-[9px] mono uppercase tracking-widest border bg-white">
                    {shipping.freight?.configured
                      ? (shipping.freight.health?.ok ? "live · awaiting rate activation" : "auth fail")
                      : "not configured"}
                  </span>
                </div>
                {shipping.freight?.configured ? (
                  <>API auth ✓ · {shipping.freight_shipments} shipments booked. Warp's pricing matrix is being activated for your account — once enabled, freight quotes will return real rates from 20,000+ carriers (LTL · FTL · box truck · cargo van). Email <a href="mailto:api-support@wearewarp.com" className="underline">api-support@wearewarp.com</a> to confirm rate activation status.</>
                ) : (
                  <>Routes for shipments ≥150 lb auto-select the freight provider when configured.</>
                )}
              </div>
            </>
          )}
        </IntegrationRow>
      </div>
    </>
  );
}

function StatusChip({ status }) {
  const m = {
    live:  { l: "Live",        c: "bg-emerald-50 text-emerald-700 border-emerald-200", i: Zap },
    wired: { l: "Wired · idle", c: "bg-blue-50 text-blue-700 border-blue-200", i: Plug },
    mock:  { l: "Mock data",    c: "bg-amber-50 text-amber-700 border-amber-200", i: Clock },
  }[status] || { l: status, c: "bg-secondary border-border", i: Clock };
  const Icon = m.i;
  return (
    <span className={`chip border ${m.c} text-[11px] inline-flex items-center gap-1`}>
      <Icon className="w-3 h-3"/>{m.l}
    </span>
  );
}

function Tile({ l, v }) {
  return (
    <div className="rounded-xl border border-border bg-secondary/30 p-3">
      <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">{l}</div>
      <div className="display text-xl mt-1">{v}</div>
    </div>
  );
}

function IntegrationRow({ icon: Icon, name, desc, status, provider, children }) {
  return (
    <div className="rounded-2xl bg-white border border-border p-6" data-testid={`integ-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-start gap-4 mb-4">
        <div className="w-11 h-11 rounded-xl bg-secondary grid place-items-center shrink-0">
          <Icon className="w-5 h-5 text-foreground/65"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="display text-lg">{name}</div>
            <StatusChip status={status}/>
          </div>
          <div className="text-sm text-foreground/65 mt-0.5">{desc}</div>
          <div className="text-[10px] mono uppercase tracking-widest text-foreground/45 mt-1">
            provider: {provider}
          </div>
        </div>
      </div>
      {children}
    </div>
  );
}

function EnvOverview({ env }) {
  if (!env) {
    return (
      <div className="rounded-2xl bg-white border border-border p-6 animate-pulse h-32" data-testid="env-overview-loading"/>
    );
  }
  const tone = {
    live:         { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-900", chip: "bg-emerald-600 text-white", Icon: ShieldCheck, title: "Production Mode", body: "All money & shipping providers are using LIVE keys. Real charges, real labels, real ACH." },
    test:         { bg: "bg-blue-50",    border: "border-blue-200",    text: "text-blue-900",    chip: "bg-blue-600 text-white",    Icon: FlaskConical, title: "Test Mode",       body: "All providers are sandboxed. Safe for demos — no real money or shipments." },
    mixed:        { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-900",   chip: "bg-amber-600 text-white",   Icon: AlertTriangle, title: "Mixed Mode",      body: "Some providers are LIVE while others are on TEST keys. Verify each row before processing transactions." },
    unconfigured: { bg: "bg-secondary",  border: "border-border",      text: "text-foreground/70", chip: "bg-foreground/70 text-background", Icon: Plug, title: "Not Configured", body: "No payment or shipping integrations have keys set yet." },
  }[env.overall] || {};
  const Icon = tone.Icon || Plug;
  return (
    <div className={`rounded-2xl border p-6 ${tone.bg} ${tone.border} ${tone.text}`} data-testid="env-overview">
      <div className="flex items-start gap-4 mb-5">
        <div className="w-11 h-11 rounded-xl bg-white/70 grid place-items-center shrink-0">
          <Icon className="w-5 h-5"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="display text-lg">{tone.title}</div>
            <span className={`chip text-[10.5px] mono uppercase tracking-widest ${tone.chip}`} data-testid="env-overall-mode">
              {env.overall}
            </span>
            <span className="chip text-[10px] mono uppercase tracking-widest border bg-white/80">
              app_env: {env.app_env}
            </span>
          </div>
          <div className="text-sm mt-1 leading-relaxed">{tone.body}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5" data-testid="env-overview-grid">
        {Object.entries(env.integrations).map(([key, v]) => <ModePill key={key} testId={key} label={v.label} mode={v.mode}/>)}
      </div>
    </div>
  );
}

function ModePill({ label, mode, testId }) {
  const m = {
    live:         { c: "bg-emerald-600 text-white", l: "LIVE" },
    test:         { c: "bg-blue-600 text-white",     l: "TEST" },
    unknown:      { c: "bg-amber-600 text-white",    l: "?" },
    unconfigured: { c: "bg-foreground/30 text-background", l: "OFF" },
  }[mode] || { c: "bg-foreground/30 text-background", l: mode?.toUpperCase() || "—" };
  return (
    <div className="rounded-xl bg-white/70 border border-white/50 px-3 py-2.5 flex items-center justify-between gap-3" data-testid={`env-pill-${testId}`}>
      <span className="text-[12.5px] truncate">{label}</span>
      <span className={`mono text-[10px] tracking-widest uppercase rounded-full px-2 py-0.5 ${m.c}`}>{m.l}</span>
    </div>
  );
}


function AutoGrantPanel({ current, onSaved }) {
  const [cfg, setCfg] = useState(current);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (current) setCfg(current); }, [current]);
  if (!cfg) return null;
  const set = (k, v) => setCfg(c => ({ ...c, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/integrations/ai/auto-grant", {
        enabled: !!cfg.enabled,
        min_confidence: Number(cfg.min_confidence),
        max_limit_usd: Number(cfg.max_limit_usd),
        max_terms: cfg.max_terms,
        allowed_decisions: cfg.allowed_decisions || ["approve"],
      });
      toast.success(data.config.enabled ? "Auto-grant enabled" : "Auto-grant saved");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally { setSaving(false); }
  };
  const tone = cfg.enabled ? "border-emerald-300 bg-emerald-50" : "border-border bg-white";
  return (
    <div className={`rounded-xl border p-4 ${tone}`} data-testid="autograntpanel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="display text-sm flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-accent"/> Auto-grant rules engine
            {cfg.enabled
              ? <span className="chip text-[10px] mono uppercase tracking-widest bg-emerald-600 text-white" data-testid="autograntenabled">ON</span>
              : <span className="chip text-[10px] mono uppercase tracking-widest bg-foreground/30 text-background" data-testid="autograntdisabled">OFF</span>}
          </div>
          <div className="text-[11.5px] text-foreground/60 mt-0.5">
            When enabled, buyers whose AI memo clears these thresholds get a credit limit raised <strong>automatically</strong> — no admin click.
          </div>
        </div>
        <label className="relative inline-flex items-center cursor-pointer" data-testid="autoGranttoggle">
          <input type="checkbox" className="sr-only peer" checked={!!cfg.enabled} onChange={e => set("enabled", e.target.checked)}/>
          <div className="w-11 h-6 bg-secondary peer-checked:bg-emerald-600 rounded-full transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"/>
        </label>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
        <div>
          <div className="text-[10px] mono uppercase tracking-widest text-foreground/55">Min confidence</div>
          <div className="flex items-center gap-2 mt-1">
            <input type="range" min="0.5" max="0.99" step="0.01" value={cfg.min_confidence}
              onChange={e => set("min_confidence", parseFloat(e.target.value))}
              data-testid="autograntconfidence" className="flex-1 accent-foreground"/>
            <span className="display text-sm w-12 text-right">{(cfg.min_confidence * 100).toFixed(0)}%</span>
          </div>
        </div>
        <div>
          <div className="text-[10px] mono uppercase tracking-widest text-foreground/55">Max limit (USD)</div>
          <input type="number" min="0" step="1000" value={cfg.max_limit_usd}
            onChange={e => set("max_limit_usd", parseInt(e.target.value || 0, 10))}
            data-testid="autograntmaxlimit"
            className="mt-1 w-full h-9 px-2 rounded-md border border-border bg-background text-sm display"/>
        </div>
        <div>
          <div className="text-[10px] mono uppercase tracking-widest text-foreground/55">Max terms</div>
          <select value={cfg.max_terms} onChange={e => set("max_terms", e.target.value)}
            data-testid="autograntmaxterms"
            className="mt-1 w-full h-9 px-2 rounded-md border border-border bg-background text-sm">
            <option value="net14">Net-14</option>
            <option value="net30">Net-30</option>
            <option value="net60">Net-60</option>
            <option value="net90">Net-90</option>
          </select>
        </div>
        <div>
          <div className="text-[10px] mono uppercase tracking-widest text-foreground/55">Decisions allowed</div>
          <select value={(cfg.allowed_decisions || ["approve"]).join(",")}
            onChange={e => set("allowed_decisions", e.target.value.split(","))}
            data-testid="autograntdecisions"
            className="mt-1 w-full h-9 px-2 rounded-md border border-border bg-background text-sm">
            <option value="approve">approve only</option>
            <option value="approve,manual_review">approve + manual_review</option>
          </select>
        </div>
      </div>
      <div className="flex justify-end mt-3">
        <Button onClick={save} disabled={saving} data-testid="autograntsave"
          className="rounded-full h-9 px-4 bg-foreground text-background hover:bg-foreground/90">
          {saving ? "Saving…" : "Save rules"}
        </Button>
      </div>
    </div>
  );
}
